import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, HeadphonesIcon, User as UserIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, STATUS_LABEL } from "@/lib/format";

const ROLE_ICON = {
  support: HeadphonesIcon,
  tenant: UserIcon,
  system: UserIcon,
};

const STATUSES = ["open", "analyzing", "answered", "resolved", "closed"];

/**
 * Reusable ticket thread viewer + reply composer.
 * Props:
 *   - ticketId: string (required when open)
 *   - open: boolean
 *   - onOpenChange: fn(bool)
 *   - onUpdated: fn(ticket) called after any change (reply, status update)
 *   - showAdminControls: if true, renders status select (super_admin only)
 *   - viewerRole: "tenant" | "support" — controls badge styling on messages
 */
export default function TicketThread({
  ticketId,
  open,
  onOpenChange,
  onUpdated,
  showAdminControls = false,
  viewerRole = "tenant",
}) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!open || !ticketId) return;
    setTicket(null);
    setReply("");
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}`);
        setTicket(data);
        // Mark as read for the viewer's side (fire-and-forget)
        api.post(`/tickets/${ticketId}/mark-read`).catch(() => {});
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Falha ao abrir ticket");
        onOpenChange(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, ticketId, onOpenChange]);

  useEffect(() => {
    if (ticket && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [ticket]);

  const sendReply = async () => {
    if (!reply.trim()) {
      toast.error("Escreva uma resposta");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/messages`, {
        text: reply,
      });
      setTicket(data);
      setReply("");
      onUpdated?.(data);
      toast.success("Resposta enviada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      const base = showAdminControls ? "/admin/tickets" : "/tickets";
      await api.patch(`${base}/${ticketId}`, { status });
      const { data } = await api.get(`/tickets/${ticketId}`);
      setTicket(data);
      onUpdated?.(data);
      toast.success("Status atualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const messages = ticket?.messages || [];
  // Fallback: if messages is empty but ticket has a `message` field (older tickets),
  // synthesize the initial message so the thread is never empty.
  const rendered = messages.length
    ? messages
    : ticket
    ? [
        {
          id: "seed",
          author_role: "tenant",
          author_name: "Cliente",
          text: ticket.message,
          created_at: ticket.created_at,
        },
      ]
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 pr-6">
            <span className="truncate">{ticket?.subject || "Ticket"}</span>
            {ticket && (
              <Badge variant="outline" className="border-stone-300 shrink-0">
                {STATUS_LABEL[ticket.status] || ticket.status}
              </Badge>
            )}
          </DialogTitle>
          {ticket && (
            <DialogDescription className="font-body text-xs text-stone-500">
              Prioridade {ticket.priority} · Aberto em {formatDateTime(ticket.created_at)}
            </DialogDescription>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-stone-400 font-body text-sm gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando conversa…
          </div>
        )}

        {!loading && ticket && (
          <>
            {/* Thread */}
            <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-2">
              {rendered.map((m) => {
                const isSupport = m.author_role === "support";
                const alignSelf =
                  (viewerRole === "support" && isSupport) ||
                  (viewerRole === "tenant" && !isSupport);
                const Icon = ROLE_ICON[m.author_role] || UserIcon;
                return (
                  <div
                    key={m.id}
                    className={`flex gap-2 ${alignSelf ? "flex-row-reverse" : ""}`}
                    data-testid={`ticket-msg-${m.id}`}
                  >
                    <div
                      className={`h-8 w-8 rounded-full shrink-0 flex items-center justify-center ${
                        isSupport
                          ? "bg-emerald-800 text-white"
                          : "bg-stone-200 text-stone-700"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className={`max-w-[75%] ${alignSelf ? "items-end" : ""} flex flex-col`}>
                      <div
                        className={`rounded-2xl px-3.5 py-2 text-sm font-body whitespace-pre-wrap break-words ${
                          isSupport
                            ? "bg-emerald-800 text-white"
                            : "bg-stone-100 text-stone-900"
                        }`}
                      >
                        {m.text}
                      </div>
                      <div className="text-[10px] text-stone-500 mt-1 font-body">
                        {m.author_name || (isSupport ? "Suporte" : "Cliente")} ·{" "}
                        {formatDateTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Admin controls */}
            {showAdminControls && (
              <div className="border-t border-stone-200 pt-3 flex items-center gap-2">
                <span className="text-xs font-body text-stone-500">Status:</span>
                <Select value={ticket.status} onValueChange={updateStatus}>
                  <SelectTrigger className="bg-white h-8 w-40 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_LABEL[s] || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Reply composer */}
            {ticket.status !== "closed" && (
              <div className="border-t border-stone-200 pt-3">
                <Textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={2}
                  placeholder={
                    showAdminControls
                      ? "Escreva sua resposta ao cliente…"
                      : "Adicione uma nova mensagem à conversa…"
                  }
                  className="bg-white resize-none"
                  data-testid="ticket-reply-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={sending}
                  className="mt-2 bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="ticket-reply-send"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? "Enviando…" : "Enviar"}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
